import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { FiArrowLeft, FiEdit2, FiTrash2, FiSave, FiX, FiAlertTriangle, FiCode } from "react-icons/fi";
import DocViewerModal from "../components/DocViewerModal";
import useUnsavedChanges from "../hooks/useUnsavedChanges";
import logo from "../assets/logo.png";

const animalImages = require.context("../assets", false, /\.webp$/);

const ANIMAL_TIERS = {
    Burbot: "Tier 1", Loch: "Tier 1", Swordfish: "Tier 1",
    Cod: "Tier 1", Goldfish: "Tier 1", Sardine: "Tier 1",
    Clownfish: "Tier 2", Shrimp: "Tier 2", Bluefish: "Tier 2",
    Catfish: "Tier 2", "Northern Pike": "Tier 2",
    Starfish: "Tier 3", Crab: "Tier 3", Dolphin: "Tier 3",
    Eel: "Tier 3", "Lake Sturgeon": "Tier 3", "Redtail Catfish": "Tier 3", Turtle: "Tier 3",
    "Blue Whale": "Tier 4", Shark: "Tier 4", Squid: "Tier 4",
    "Gray Whale": "Tier 4", Narwhal: "Tier 4", Oranda: "Tier 4",
    Anglerfish: "Tier 5", Angelshark: "Tier 5", Firefish: "Tier 5",
    Mandarinfish: "Tier 5", "Orca Whale": "Tier 5",
};

const TIER_COLORS = {
    "Tier 1": { bg: "#f0f0f0",   color: "#606060", border: "#ddd"    },
    "Tier 2": { bg: "#e8f5e9",   color: "#2e7d32", border: "#a5d6a7" },
    "Tier 3": { bg: "#e3f2fd",   color: "#1565c0", border: "#90caf9" },
    "Tier 4": { bg: "#ede7f6",   color: "#6a1b9a", border: "#ce93d8" },
    "Tier 5": { bg: "#fff8e1",   color: "#e65100", border: "#ffcc02" },
};

const tierOrder = { "Tier 1": 1, "Tier 2": 2, "Tier 3": 3, "Tier 4": 4, "Tier 5": 5 };

// ── Field row for view/edit ──────────────────────────────────────
function Field({ label, value, editing, inputType = "text", onChange }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{
                fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.07em", color: "#606060",
            }}>
                {label}
            </span>
            {editing ? (
                <input
                    type={inputType}
                    value={value}
                    onChange={e => onChange(inputType === "number" ? Number(e.target.value) : e.target.value)}
                    style={{
                        fontFamily: "'Quicksand', sans-serif", fontSize: 14,
                        padding: "8px 12px", borderRadius: 10,
                        border: "1.5px solid #E0E0E0", background: "#fff",
                        color: "#000", outline: "none", width: "100%",
                    }}
                />
            ) : (
                <span style={{ fontSize: 15, fontWeight: 600, color: "#000" }}>
                    {value || <span style={{ color: "#bbb" }}>—</span>}
                </span>
            )}
        </div>
    );
}

// ── Mini stat card ───────────────────────────────────────────────
// `icon` is either an emoji string or an imported image path (e.g. the
// Kubaya logo) — image paths are told apart by their file extension.
function StatPill({ icon, label, value, color }) {
    const isImageIcon = typeof icon === "string" && /\.(png|jpe?g|svg|gif|webp)(\?|$)/i.test(icon);
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "#fff", borderRadius: 14,
            border: "1.5px solid #E0E0E0", borderBottom: `3px solid ${color}`,
            padding: "12px 18px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
        }}>
            {isImageIcon
                ? <img src={icon} alt="" style={{ width: 22, height: 22, borderRadius: 6 }} />
                : <span style={{ fontSize: 22 }}>{icon}</span>}
            <div>
                <div style={{ fontWeight: 700, fontSize: 18, color, lineHeight: 1 }}>
                    {typeof value === "number" ? value.toLocaleString() : value}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#606060" }}>
                    {label}
                </div>
            </div>
        </div>
    );
}

export default function UserDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [user, setUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingUser, setEditingUser] = useState({
        name: "", email: "", username: "", imageurl: "",
        cups: 0, followers: 0, following: 0,
    });
    const [avatarError, setAvatarError] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    const [viewDocOpen, setViewDocOpen] = useState(false);

    // Confirm before leaving while an unsaved profile edit is open.
    useUnsavedChanges(isEditing && !saving);

    useEffect(() => {
        const fetchUser = async () => {
            const snap = await getDoc(doc(db, "users", id));
            if (snap.exists()) {
                const data = { id: snap.id, ...snap.data() };
                setUser(data);
                setEditingUser({
                    name: data.name || "", email: data.email || "",
                    username: data.username || "", imageurl: data.imageurl || "",
                    cups: data.cups || 0, followers: data.followers || 0,
                    following: data.following || 0,
                });
            }
            setLoading(false);
        };
        fetchUser();
    }, [id]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (!isEditing) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isEditing]);

    useEffect(() => {
        if (!isEditing) return;
        const handlePopState = () => {
            if (window.confirm("Leave without saving?")) {
                window.removeEventListener("popstate", handlePopState);
                window.history.back();
            } else {
                window.history.pushState(null, "", window.location.href);
            }
        };
        window.history.pushState(null, "", window.location.href);
        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [isEditing]);

    const handleSave = async () => {
        setSaving(true);
        await updateDoc(doc(db, "users", id), {
            name: editingUser.name,
            email: editingUser.email,
            username: editingUser.username,
            imageurl: editingUser.imageurl,
            cups: Number(editingUser.cups) || 0,
            followers: Number(editingUser.followers) || 0,
            following: Number(editingUser.following) || 0,
        });
        setUser(prev => prev ? { ...prev, ...editingUser } : prev);
        setAvatarError(false);
        setIsEditing(false);
        setSaving(false);
    };

    const handleCancel = () => {
        if (!user) return;
        setEditingUser({
            name: user.name || "", email: user.email || "",
            username: user.username || "", imageurl: user.imageurl || "",
            cups: user.cups || 0, followers: user.followers || 0,
            following: user.following || 0,
        });
        setAvatarError(false);
        setIsEditing(false);
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete ${user?.name || "this user"}? This deletes their account, login, and username, and cannot be undone.`)) return;
        setDeleting(true);
        setDeleteError("");
        try {
            const deleteUserAccount = httpsCallable(functions, "deleteUserAccount");
            await deleteUserAccount({ uid: id });
            navigate("/users");
        } catch (err) {
            setDeleteError(err?.message || "Failed to delete user.");
            setDeleting(false);
        }
    };

    const set = (key) => (val) => setEditingUser(u => ({ ...u, [key]: val }));

    // ── Completed lessons renderer ───────────────────────────────
    const formatTimestamp = (value) => {
        const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        const parts = new Intl.DateTimeFormat("en-US", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "numeric", minute: "2-digit", hour12: true,
        }).formatToParts(date);
        const g = (t) => parts.find(p => p.type === t)?.value || "";
        return `${g("day")} ${g("month")} ${g("year")} · ${g("hour")}:${g("minute")} ${g("dayPeriod")}`;
    };

    const parsedLesson = (lesson, idx) => {
        if (lesson == null) return { id: `Lesson ${idx + 1}`, accuracy: null, duration: null, timestamp: null };
        if (typeof lesson === "string" || typeof lesson === "number") {
            return { id: String(lesson), accuracy: null, duration: null, timestamp: null };
        }
        return {
            id: lesson.lessonId || lesson.id || `Lesson ${idx + 1}`,
            accuracy: lesson.percentAccuracy != null ? lesson.percentAccuracy : null,
            duration: lesson.duration != null ? lesson.duration : null,
            timestamp: lesson.timestamp ? formatTimestamp(lesson.timestamp) : null,
        };
    };

    // ── Animal collection ────────────────────────────────────────
    const animalCounts = user
        ? Object.entries(user)
            .filter(([key, value]) => key.endsWith("Count") && typeof value === "number")
            .map(([key, value]) => {
                const name = key.replace(/Count$/, "");
                const displayName = name.replace(/([a-z])([A-Z])/g, "$1 $2");
                const tier = ANIMAL_TIERS[displayName] || "Tier 99";
                return { name, displayName, value, tier };
            })
            .sort((a, b) => {
                const td = (tierOrder[a.tier] || 99) - (tierOrder[b.tier] || 99);
                return td !== 0 ? td : a.displayName.localeCompare(b.displayName);
            })
        : [];

    const getAnimalImage = (name) => {
        const fileName = `${name.replace(/([a-z])([A-Z])/g, "$1 $2")}.webp`;
        try { return animalImages(`./${fileName}`); } catch { return null; }
    };

    // ── Shared button styles ─────────────────────────────────────
    const btn = (bg, color, border) => ({
        backgroundColor: bg, color, borderColor: border,
        fontSize: 14, padding: "9px 20px",
        display: "inline-flex", alignItems: "center", gap: 7,
    });

    // ── Loading / not found ──────────────────────────────────────
    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12, color: "#606060", fontWeight: 600, fontFamily: "'Quicksand', sans-serif" }}>
                <div style={{ width: 20, height: 20, border: "2.5px solid #E0E0E0", borderTop: "2.5px solid #2a9d8f", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                Loading user…
            </div>
        );
    }

    if (!user) {
        return (
            <div style={{ fontFamily: "'Quicksand', sans-serif" }}>
                <h1>User not found</h1>
                <button className="primary-button" onClick={() => navigate("/users")} style={btn("#2a9d8f", "#fff", "#258D80")}>
                    <FiArrowLeft size={15} /> Back to Users
                </button>
            </div>
        );
    }

    const displayName  = isEditing ? editingUser.name     : user.name;
    const displayUrl   = isEditing ? editingUser.imageurl : user.imageurl;
    const completedLessons = Array.isArray(user.completedLessons) ? user.completedLessons : [];
    const initials = (displayName || "?").split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase();

    return (
        <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 1100 }}>

            {/* ── Page header ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <button
                        onClick={() => navigate("/users")}
                        style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "none", border: "1.5px solid #E0E0E0", borderBottomWidth: 3,
                            borderRadius: 10, padding: "8px 14px",
                            fontFamily: "'Quicksand', sans-serif", fontWeight: 700,
                            fontSize: 13, color: "#606060", cursor: "pointer",
                            transition: "transform 80ms ease, border-bottom-width 80ms ease",
                        }}
                        onMouseDown={e => { e.currentTarget.style.transform = "translateY(2px)"; e.currentTarget.style.borderBottomWidth = "1.5px"; }}
                        onMouseUp={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.borderBottomWidth = "3px"; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.borderBottomWidth = "3px"; }}
                    >
                        <FiArrowLeft size={15} /> Back
                    </button>
                    <h1 style={{ margin: 0 }}>{displayName || "User Details"}</h1>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button className="primary-button" onClick={() => setViewDocOpen(true)} style={btn("#fff", "#3a82c6", "#3a82c6")}>
                        <FiCode size={14} /> View JSON
                    </button>
                    {!isEditing ? (
                        <>
                            <button className="primary-button" onClick={() => setIsEditing(true)} style={btn("#e9c46a", "#fff", "#d4b15f")}>
                                <FiEdit2 size={14} /> Edit Profile
                            </button>
                            <button className="primary-button" onClick={handleDelete} disabled={deleting} style={btn("#e76f51", "#fff", "#cf6348")}>
                                <FiTrash2 size={14} /> {deleting ? "Deleting…" : "Delete User"}
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="primary-button" onClick={handleSave} disabled={saving} style={btn("#2a9d8f", "#fff", "#258D80")}>
                                <FiSave size={14} /> {saving ? "Saving…" : "Save Changes"}
                            </button>
                            <button className="primary-button" onClick={handleCancel} disabled={saving} style={btn("#fff", "#606060", "#cccccc")}>
                                <FiX size={14} /> Cancel
                            </button>
                        </>
                    )}
                </div>
            </div>

            {deleteError && (
                <div style={{ color: "#e76f51", fontSize: 13, fontWeight: 600, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
                    <FiAlertTriangle size={14} /> {deleteError}
                </div>
            )}

            {/* ── Profile card + stat pills ── */}
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 24, alignItems: "flex-start" }}>

                {/* Profile card */}
                <div style={{
                    background: "#fff", borderRadius: 20,
                    border: `2px solid ${isEditing ? "#2a9d8f" : "#E0E0E0"}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                    padding: 28, flex: "1 1 340px", minWidth: 300,
                    transition: "border-color 0.2s ease",
                }}>
                    {/* Avatar + identity */}
                    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
                        {displayUrl && !avatarError ? (
                            <img
                                src={displayUrl}
                                alt={displayName}
                                onError={() => setAvatarError(true)}
                                style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", border: "3px solid #E0E0E0", flexShrink: 0 }}
                            />
                        ) : (
                            <div style={{
                                width: 72, height: 72, borderRadius: "50%", flexShrink: 0,
                                background: "#2a9d8f", color: "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 24, fontWeight: 700, fontFamily: "'Quicksand', sans-serif",
                                border: "3px solid #258D80",
                            }}>
                                {initials}
                            </div>
                        )}
                        <div>
                            <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
                                {displayName || "Unnamed User"}
                            </div>
                            {(isEditing ? editingUser.username : user.username) && (
                                <div style={{ fontSize: 14, color: "#606060", fontWeight: 500, marginTop: 3 }}>
                                    @{isEditing ? editingUser.username : user.username}
                                </div>
                            )}
                            <span style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                marginTop: 6, borderRadius: 999, padding: "2px 10px",
                                fontSize: 11, fontWeight: 700,
                                background: user.verified ? "#50C87818" : "#f0f0f0",
                                color: user.verified ? "#27774a" : "#888",
                                border: `1px solid ${user.verified ? "#50C878" : "#E0E0E0"}`,
                            }}>
                                {user.verified ? "✓ Verified" : "Unverified"}
                            </span>
                        </div>
                    </div>

                    {/* Fields */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <Field label="Name"      value={editingUser.name}     editing={isEditing} onChange={set("name")} />
                        <Field label="Username"  value={editingUser.username} editing={isEditing} onChange={set("username")} />
                        <Field label="Email"     value={editingUser.email}    editing={isEditing} onChange={set("email")} />
                        {isEditing && (
                            <Field label="Avatar URL" value={editingUser.imageurl} editing onChange={set("imageurl")} />
                        )}
                        <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                            <Field label="Cups"      value={editingUser.cups}      inputType="number" editing={isEditing} onChange={set("cups")} />
                            <Field label="Followers" value={editingUser.followers} inputType="number" editing={isEditing} onChange={set("followers")} />
                            <Field label="Following" value={editingUser.following} inputType="number" editing={isEditing} onChange={set("following")} />
                        </div>
                    </div>
                </div>

                {/* Stat pills */}
                <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: "0 0 auto", minWidth: 200 }}>
                    <StatPill icon={logo} label="Cups"        value={user.cups ?? 0}                 color="#2a9d8f" />
                    <StatPill icon="👥" label="Followers"     value={user.followers ?? 0}            color="#3a82c6" />
                    <StatPill icon="👁️"  label="Following"     value={user.following ?? 0}            color="#9b59b6" />
                    <StatPill icon="📚" label="Lessons Done"  value={completedLessons.length}        color="#e76f51" />
                </div>
            </div>

            {/* ── Completed Lessons ── */}
            {completedLessons.length > 0 && (
                <div style={{
                    background: "#fff", borderRadius: 20, border: "1px solid #E0E0E0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)", marginBottom: 20, overflow: "hidden",
                }}>
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "16px 22px", borderBottom: "1px solid #E0E0E0",
                    }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>Completed Lessons</span>
                        <span style={{
                            background: "#2a9d8f", color: "#fff", borderRadius: 999,
                            padding: "3px 12px", fontSize: 12, fontWeight: 700,
                        }}>
                            {completedLessons.length}
                        </span>
                    </div>
                    <div style={{ maxHeight: 340, overflowY: "auto" }}>
                        {completedLessons.map((lesson, idx) => {
                            const { id: lessonId, accuracy, duration, timestamp } = parsedLesson(lesson, idx);
                            const isLast = idx === completedLessons.length - 1;
                            return (
                                <div
                                    key={`${lessonId}-${idx}`}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 16,
                                        padding: "11px 22px",
                                        borderBottom: isLast ? "none" : "1px solid #f3f3f3",
                                    }}
                                >
                                    <div style={{
                                        width: 28, height: 28, borderRadius: "50%",
                                        background: "#2a9d8f18", color: "#2a9d8f",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 11, fontWeight: 700, flexShrink: 0,
                                    }}>
                                        {idx + 1}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {lessonId}
                                        </div>
                                        {timestamp && (
                                            <div style={{ fontSize: 12, color: "#606060", marginTop: 2 }}>{timestamp}</div>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                                        {accuracy !== null && (
                                            <span style={{
                                                background: accuracy >= 80 ? "#50C87818" : accuracy >= 50 ? "#e9c46a18" : "#e76f5118",
                                                color: accuracy >= 80 ? "#27774a" : accuracy >= 50 ? "#b8890a" : "#c0392b",
                                                border: `1px solid ${accuracy >= 80 ? "#50C878" : accuracy >= 50 ? "#e9c46a" : "#e76f51"}`,
                                                borderRadius: 999, padding: "2px 10px",
                                                fontSize: 12, fontWeight: 700,
                                            }}>
                                                {accuracy}%
                                            </span>
                                        )}
                                        {duration !== null && (
                                            <span style={{
                                                background: "#f5f5f5", color: "#606060",
                                                border: "1px solid #E0E0E0",
                                                borderRadius: 999, padding: "2px 10px",
                                                fontSize: 12, fontWeight: 600,
                                            }}>
                                                {duration}s
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Animal Collection ── */}
            {animalCounts.length > 0 && (
                <div style={{
                    background: "#fff", borderRadius: 20, border: "1px solid #E0E0E0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden",
                }}>
                    <div style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "16px 22px", borderBottom: "1px solid #E0E0E0",
                    }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>Animal Collection</span>
                        <span style={{
                            background: "#e9c46a", color: "#fff", borderRadius: 999,
                            padding: "3px 12px", fontSize: 12, fontWeight: 700,
                        }}>
                            {animalCounts.length} animals
                        </span>
                    </div>
                    <div style={{ padding: "18px 22px" }}>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                            gap: 12,
                        }}>
                            {animalCounts.map(({ name, displayName, value, tier }) => {
                                const img = getAnimalImage(name);
                                const tc = TIER_COLORS[tier] || TIER_COLORS["Tier 1"];
                                return (
                                    <div
                                        key={name}
                                        style={{
                                            background: "#fff", borderRadius: 14,
                                            border: "1.5px solid #E0E0E0",
                                            padding: "14px 12px",
                                            display: "flex", flexDirection: "column",
                                            alignItems: "center", gap: 8,
                                            textAlign: "center",
                                        }}
                                    >
                                        {img ? (
                                            <img src={img} alt={displayName} style={{ width: 48, height: 48, objectFit: "contain" }} />
                                        ) : (
                                            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
                                                🐟
                                            </div>
                                        )}
                                        <div style={{ fontWeight: 700, fontSize: 13 }}>{displayName}</div>
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                                            <span style={{
                                                fontSize: 11, fontWeight: 700, borderRadius: 999,
                                                padding: "2px 8px",
                                                background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`,
                                            }}>
                                                {tier}
                                            </span>
                                            <span style={{ fontWeight: 700, fontSize: 16, color: "#2a9d8f" }}>
                                                ×{value}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <DocViewerModal
                open={viewDocOpen}
                onClose={() => setViewDocOpen(false)}
                path={`users/${id}`}
                data={user}
            />
        </div>
    );
}
